// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/sales/settings/ProgramList.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Plus, Trash2, Search, X, Loader2, RefreshCw, 
  CheckCircle2, XCircle, Eye, Layers, IndianRupee, 
  Edit, ExternalLink 
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../../common/apiClient';
import SummaryApi from '../../../../common/SummaryApi';
import SelectDropdown from '../../../../customStyles/SelectDropdown';
import DeleteConfirmationOffcanvas from '../../../../helpers/DeleteConfirmationOffcanvas';

const OFFERING_TYPE_LABELS = {
  sports_program: { label: 'Sports Program', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  course: { label: 'Course', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  workshop: { label: 'Workshop', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  subscription: { label: 'Subscription', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  physical_product: { label: 'Physical Product', color: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const ProgramList = () => {
  const navigate = useNavigate();
  const { portalName } = useParams();

  // State Management
  const [programs, setPrograms] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Delete Offcanvas State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    item: null,
    loading: false,
  });

  // Fetch Divisions for filter dropdown
  const fetchDivisions = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getAllDivisions.url);
      if (response.data?.success) {
        setDivisions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching divisions:', error);
    }
  }, []);

  // Fetch All Programs
  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getAllPrograms.url);
      if (response.data?.success) {
        setPrograms(response.data.data || []);
      } else {
        toast.error(response.data?.message || 'Failed to fetch programs');
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch offerings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDivisions();
    fetchPrograms();
  }, [fetchDivisions, fetchPrograms]);

  // Delete Confirmation Handler
  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;

    setDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      const url = typeof SummaryApi.deleteProgram.url === 'function'
        ? SummaryApi.deleteProgram.url(deleteModal.item._id)
        : SummaryApi.deleteProgram.url;

      const response = await api.delete(url);
      if (response.data?.success) {
        toast.success('Program and Cloudinary images deleted successfully');
        setDeleteModal({ isOpen: false, item: null, loading: false });
        fetchPrograms();
      } else {
        toast.error(response.data?.message || 'Delete operation failed');
      }
    } catch (error) {
      console.error('Error deleting program:', error);
      toast.error(error.response?.data?.message || 'Failed to delete program');
    } finally {
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Client-side Filtering
  const filteredPrograms = useMemo(() => {
    return programs.filter((item) => {
      const matchesSearch =
        item.name?.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
        item.code?.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
        item.divisionId?.name?.toLowerCase().includes(searchTerm.toLowerCase().trim());

      const matchesDivision =
        divisionFilter === 'all' ? true : item.divisionId?._id === divisionFilter;

      const matchesType =
        typeFilter === 'all' ? true : item.offeringType === typeFilter;

      const matchesStatus =
        statusFilter === 'all' ? true :
        statusFilter === 'active' ? item.isActive === true :
        item.isActive === false;

      return matchesSearch && matchesDivision && matchesType && matchesStatus;
    });
  }, [programs, searchTerm, divisionFilter, typeFilter, statusFilter]);

  return (
    <div className="p-2">
      {/* Top Header Card */}
      <div className="p-4 mb-3 bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-50 text-red-600 rounded-lg">
              <Layers className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Programs & Offerings</h1>
              <p className="text-xs md:text-sm text-gray-500">
                Manage coaching programs, subscriptions, workshops, courses, and products
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate(`/${portalName}/sales/settings/programs/create`)}
            className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add New Offering</span>
          </button>
        </div>

        {/* Filter and Search Bar */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3 pt-4 border-t border-gray-100">
          {/* Search Box */}
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-all"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Division Filter */}
          <SelectDropdown
            options={[{ _id: 'all', name: 'All Divisions' }, ...divisions]}
            value={divisionFilter}
            onChange={(val) => setDivisionFilter(val)}
            placeholder="Select Division"
            valueKey="_id"
            labelKey="name"
            searchable={true}
          />

          {/* Offering Type Filter */}
          <SelectDropdown
            options={[
              { value: 'all', label: 'All Offering Types' },
              { value: 'sports_program', label: 'Sports Program' },
              { value: 'course', label: 'Course' },
              { value: 'workshop', label: 'Workshop' },
              { value: 'subscription', label: 'Subscription' },
              { value: 'physical_product', label: 'Physical Product' },
            ]}
            value={typeFilter}
            onChange={(val) => setTypeFilter(val)}
            placeholder="Filter by Type"
            valueKey="value"
            labelKey="label"
            searchable={false}
          />

          {/* Status and Refresh */}
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <SelectDropdown
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                placeholder="Select Status"
                valueKey="value"
                labelKey="label"
                searchable={false}
              />
            </div>
            <button
              onClick={fetchPrograms}
              disabled={loading}
              className="p-2 border border-gray-300 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Program Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12">
            <Loader2 className="w-8 h-8 text-red-600 animate-spin mb-3" />
            <p className="text-sm text-gray-500">Loading programs and offerings...</p>
          </div>
        ) : filteredPrograms.length === 0 ? (
          <div className="text-center p-12">
            <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-700">No offerings found</h3>
            <p className="text-xs text-gray-400 mt-1">
              {searchTerm || divisionFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your search or filter options'
                : 'Click "Add New Offering" to create your first program'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-gray-600 font-medium">
                <tr>
                  <th className="py-3.5 px-4 w-12 text-center">S.No</th>
                  <th className="py-3.5 px-4">Offering Name</th>
                  <th className="py-3.5 px-4">Division</th>
                  <th className="py-3.5 px-4">Type</th>
                  <th className="py-3.5 px-4">Pricing Plans</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-center w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredPrograms.map((prog, idx) => {
                  const typeBadge = OFFERING_TYPE_LABELS[prog.offeringType] || {
                    label: prog.offeringType,
                    color: 'bg-gray-100 text-gray-700 border-gray-200',
                  };

                  //  Fixed: Extract image URL safely from images[0].url or legacy string
                  const displayImageUrl = prog.images?.[0]?.url || (typeof prog.images?.[0] === 'string' ? prog.images[0] : null);

                  return (
                    <tr key={prog._id} className="hover:bg-gray-50/70 transition-colors">
                      <td className="py-3.5 px-4 text-center text-gray-500">{idx + 1}</td>

                      {/*  Fixed: Name, Cloudinary Image & Optional Website Link */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          {displayImageUrl ? (
                            <img
                              src={displayImageUrl}
                              alt={prog.name}
                              className="w-10 h-10 object-cover rounded-lg border border-gray-200 shadow-xs"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gray-100 text-gray-400 rounded-lg flex items-center justify-center text-xs font-bold uppercase">
                              {prog.name?.slice(0, 2)}
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-gray-900">{prog.name}</span>
                              {/*  Added: Quick website link if redirect URL is configured */}
                              {prog.websiteRedirectUrl && (
                                <a
                                  href={prog.websiteRedirectUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Open Public Website Link"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                            {prog.code && (
                              <span className="inline-block text-xs font-mono text-gray-400">
                                {prog.code}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Division */}
                      <td className="py-3.5 px-4">
                        <span className="font-medium text-gray-700">
                          {prog.divisionId?.name || '-'}
                        </span>
                      </td>

                      {/* Offering Type Badge */}
                      <td className="py-3.5 px-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${typeBadge.color}`}>
                          {typeBadge.label}
                        </span>
                      </td>

                      {/* Pricing Summary */}
                      <td className="py-3.5 px-4">
                        <div className="flex flex-col gap-1 max-w-xs">
                          {prog.pricingPlans?.map((p, pIdx) => (
                            <div key={pIdx} className="text-xs text-gray-600 flex items-center gap-1.5">
                              <span className="font-semibold text-gray-800">{p.planName}:</span>
                              {p.discountType !== 'none' && p.discountValue > 0 && (
                                <span className="line-through text-gray-400">
                                  ₹{p.originalPrice?.toLocaleString('en-IN')}
                                </span>
                              )}
                              <span className="text-emerald-700 font-bold flex items-center">
                                <IndianRupee className="w-3 h-3" />
                                {p.totalAmount?.toLocaleString('en-IN')}
                              </span>
                              <span className="text-[10px] text-gray-400 uppercase">
                                ({p.billingCycle.replace('_', ' ')})
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          prog.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {prog.isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {prog.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Action Buttons */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => navigate(`/${portalName}/sales/settings/programs/view/${prog._id}`)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/${portalName}/sales/settings/programs/edit/${prog._id}`)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Edit Offering"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteModal({ isOpen: true, item: prog, loading: false })}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Offering"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Offcanvas */}
      <DeleteConfirmationOffcanvas
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null, loading: false })}
        onConfirm={handleDeleteConfirm}
        title="Delete Program / Offering"
        itemName={deleteModal.item?.name || 'this offering'}
        isLoading={deleteModal.loading}
        confirmButtonText="Delete Offering"
      />
    </div>
  );
};

export default ProgramList;